import { Loader2, RefreshCw, Check, Copy, Pencil, Save, X, Clock } from 'lucide-react'
import { RewriteContent, RewriteChannel } from '@/services/api'
import clsx from 'clsx'

interface GenerationTabProps {
  contents: RewriteContent[]
  channels: RewriteChannel[]
  activeChannelId: string | null
  activeSectionIndex: number
  // editor state
  editingSection: { contentId: string; index: number } | null
  editText: string
  rewritingSection: { contentId: string; index: number } | null
  copiedId: string | null
  onEditTextChange: (text: string) => void
  onSave: (contentId: string, index: number) => void
  onRewrite: (contentId: string, index: number) => void
  onCopy: (text: string, id: string) => void
  onStartEdit: (contentId: string, index: number, body: string) => void
  onCancelEdit: () => void
}

export function GenerationTab({
  contents, channels, activeChannelId, activeSectionIndex,
  editingSection, editText, rewritingSection, copiedId,
  onEditTextChange, onSave, onRewrite, onCopy, onStartEdit, onCancelEdit,
}: GenerationTabProps) {
  // Filter contents for this channel
  const channelContents = contents
    .filter(c => c.channel_id === activeChannelId)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

  if (channelContents.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-text-tertiary p-6">
        <Clock className="w-12 h-12 mb-3 opacity-30" />
        <p className="text-sm font-medium">아직 생성된 콘텐츠가 없습니다</p>
        <p className="text-xs mt-1">상단의 생성 버튼을 클릭하세요</p>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      {channelContents.map((content, contentIdx) => {
        const sections = typeof content.sections === 'string' ? JSON.parse(content.sections) : content.sections
        const sectionArr = sections as Array<{ type: string; title: string; body: string }>
        const section = sectionArr?.[activeSectionIndex]
        if (!section) return null

        const isEditing = editingSection?.contentId === content.id && editingSection?.index === activeSectionIndex
        const isRewriting = rewritingSection?.contentId === content.id && rewritingSection?.index === activeSectionIndex
        const sectionId = `${content.id}-${activeSectionIndex}`
        const d = new Date(content.created_at)
        const timeStr = d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })

        const ch = channels.find(c => c.id === content.channel_id)
        const structure = ch ? (typeof ch.structure === 'string' ? JSON.parse(ch.structure) : ch.structure) : null
        const maxLength = (structure as { sections?: Array<{ maxLength?: number }> })?.sections?.[activeSectionIndex]?.maxLength
        const charCount = section.body?.length || 0
        const isOverLimit = maxLength ? charCount > maxLength : false

        return (
          <div key={content.id} className={clsx(
            'border rounded-lg overflow-hidden',
            contentIdx === 0 ? 'border-primary/30' : 'border-border-subtle'
          )}>
            {/* Generation header */}
            <div className="px-3 py-2 bg-bg-secondary/30 flex items-center justify-between border-b border-border-subtle">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-text-secondary">{timeStr}</span>
                {contentIdx === 0 && <span className="text-xs px-1.5 py-0.5 rounded bg-primary/20 text-primary font-medium">최신</span>}
                <span className={clsx('text-xs px-1.5 py-0.5 rounded font-medium',
                  content.status === 'published' ? 'bg-green-500/20 text-green-400' :
                  content.status === 'review' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-gray-500/20 text-gray-400'
                )}>
                  {content.status === 'published' ? '확정' : content.status === 'review' ? '검토중' : '초안'}
                </span>
              </div>
              <div className="flex items-center gap-0.5">
                <button onClick={() => onCopy(section.body, sectionId)} className="p-1 rounded hover:bg-bg-elevated text-text-tertiary hover:text-text-primary" title="복사">
                  {copiedId === sectionId ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                </button>
                <button onClick={() => onRewrite(content.id, activeSectionIndex)} disabled={isRewriting} className="p-1 rounded hover:bg-bg-elevated text-text-tertiary hover:text-text-primary" title="AI 재생성">
                  {isRewriting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                </button>
                <button onClick={() => {
                  if (isEditing) onCancelEdit(); else onStartEdit(content.id, activeSectionIndex, section.body)
                }} className="p-1 rounded hover:bg-bg-elevated text-text-tertiary hover:text-text-primary" title="편집">
                  <Pencil className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Section body */}
            <div className="p-3">
              {isEditing ? (
                <div className="space-y-2">
                  <textarea
                    value={editText}
                    onChange={(e) => onEditTextChange(e.target.value)}
                    rows={6}
                    className="w-full bg-bg-elevated border border-border-subtle rounded-lg px-3 py-2 text-sm resize-y"
                  />
                  <div className="flex items-center justify-between">
                    <span className={clsx('text-xs', maxLength && editText.length > maxLength ? 'text-red-400' : 'text-text-tertiary')}>
                      {editText.length}{maxLength ? `/${maxLength}` : ''}자
                    </span>
                    <div className="flex gap-2">
                      <button onClick={onCancelEdit} className="btn btn-ghost text-xs flex items-center gap-1"><X className="w-3 h-3" /> 취소</button>
                      <button onClick={() => onSave(content.id, activeSectionIndex)} className="btn btn-primary text-xs flex items-center gap-1"><Save className="w-3 h-3" /> 저장</button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{section.body}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={clsx('text-xs', isOverLimit ? 'text-red-400' : 'text-text-tertiary')}>
                      {charCount}{maxLength ? `/${maxLength}` : ''}자
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
